import React, { useState, useEffect } from 'react';
import {
    Form, Input, Button, Table, Space, Popconfirm, Empty, Spin, Divider,
    Card, Row, Col, Select, Checkbox, InputNumber, Tag, Tooltip, Alert
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, CheckOutlined } from '@ant-design/icons';
import { useSnackbar } from 'notistack';
import {
    getCostVariables, getCalculationRules, getActiveCalculationRule,
    createCalculationRule, updateCalculationRule, deleteCalculationRule,
} from '../apiCalls';
import * as Sentry from "@sentry/react";
import './RuleBuilder.css';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageLayout from './PageLayout';
import { usePageLayout } from '../hooks/usePageLayout';

const RuleBuilder = () => {
    const { t } = useTranslation();
    const { propertyID } = useParams();
    const pageLayoutProps = usePageLayout();
    const [form] = Form.useForm();
    const [rules, setRules] = useState([]);
    const [variables, setVariables] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fetchLoading, setFetchLoading] = useState(false);
    const [isBuilding, setIsBuilding] = useState(false);
    const [editingRuleId, setEditingRuleId] = useState(null);
    const [activeRule, setActiveRule] = useState(null);
    const [ruleSteps, setRuleSteps] = useState([]);
    const [calculationOrder, setCalculationOrder] = useState(['baseAmount', 'margin', 'vat']);
    const [phases, setPhases] = useState({ baseCalculation: [], marginApplied: [], vatApplied: [], excluded: [] });
    const { enqueueSnackbar } = useSnackbar();

    useEffect(() => {
        if (propertyID) {
            fetchData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [propertyID]);

    const fetchData = async () => {
        setFetchLoading(true);
        try {
            const [variablesData, rulesData, activeRuleData] = await Promise.all([
                getCostVariables(propertyID),
                getCalculationRules(propertyID),
                getActiveCalculationRule(propertyID).catch(() => null)
            ]);

            setVariables(variablesData);
            setRules(rulesData);
            setActiveRule(activeRuleData);
        } catch (err) {
            if (err.response?.status !== 404) {
                enqueueSnackbar(t('rules.error.loading'), { variant: 'error' });
                Sentry.captureException(err);
            }
        } finally {
            setFetchLoading(false);
        }
    };

    const handleCreateNew = () => {
        form.resetFields();
        setIsBuilding(true);
        setEditingRuleId(null);
        setRuleSteps([]);
        setPhases({ baseCalculation: [], marginApplied: [], vatApplied: [], excluded: [] });
        setCalculationOrder(['baseAmount', 'margin', 'vat']);
    };

    const handleEditRule = (rule) => {
        form.setFieldsValue({
            name: rule.name,
            description: rule.description
        });
        setIsBuilding(true);
        setEditingRuleId(rule._id);
        setRuleSteps(rule.ruleChain || []);
        setPhases(rule.phases || { baseCalculation: [], marginApplied: [], vatApplied: [], excluded: [] });
        setCalculationOrder(rule.calculationOrder || ['baseAmount', 'margin', 'vat']);
    };

    const addStep = (stepType) => {
        const newStep = {
            id: `step_${Date.now()}`,
            step: ruleSteps.length + 1,
            name: `${stepType} Step ${ruleSteps.length + 1}`,
            type: stepType,
            variables: [],
            output: `result_${ruleSteps.length + 1}`,
            enabled: true,
            label: ''
        };
        setRuleSteps([...ruleSteps, newStep]);
    };

    const updateStep = (stepId, updates) => {
        setRuleSteps(ruleSteps.map(s => s.id === stepId ? { ...s, ...updates } : s));
    };

    const removeStep = (stepId) => {
        setRuleSteps(ruleSteps.filter(s => s.id !== stepId));
    };

    const handleSaveRule = async (values) => {
        if (ruleSteps.length === 0) {
            enqueueSnackbar(t('rules.error.noSteps'), { variant: 'warning' });
            return;
        }

        setLoading(true);
        try {
            const ruleData = {
                name: values.name,
                description: values.description,
                ruleChain: ruleSteps,
                phases,
                calculationOrder,
                isActive: editingRuleId ? activeRule?._id === editingRuleId : false
            };

            if (editingRuleId) {
                await updateCalculationRule(editingRuleId, ruleData.name, ruleData.description, ruleData.ruleChain, ruleData.phases, ruleData.calculationOrder, ruleData.isActive);
                enqueueSnackbar(t('rules.success.updated'), { variant: 'success' });
            } else {
                await createCalculationRule(propertyID, ruleData.name, ruleData.description, ruleData.ruleChain, ruleData.phases, ruleData.calculationOrder);
                enqueueSnackbar(t('rules.success.created'), { variant: 'success' });
            }

            setIsBuilding(false);
            setEditingRuleId(null);
            setRuleSteps([]);
            form.resetFields();
            await fetchData();
        } catch (err) {
            const errorMsg = err.response?.data || t('rules.error.saving');
            enqueueSnackbar(errorMsg, { variant: 'error' });
            Sentry.captureException(err);
        } finally {
            setLoading(false);
        }
    };

    const handleActivateRule = async (ruleId) => {
        setLoading(true);
        try {
            const rule = rules.find(r => r._id === ruleId);
            await updateCalculationRule(ruleId, rule.name, rule.description, rule.ruleChain, rule.phases, rule.calculationOrder, true);
            enqueueSnackbar(t('rules.success.activated'), { variant: 'success' });
            await fetchData();
        } catch (err) {
            enqueueSnackbar(t('rules.error.activating'), { variant: 'error' });
            Sentry.captureException(err);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteRule = async (ruleId) => {
        try {
            await deleteCalculationRule(ruleId);
            enqueueSnackbar(t('rules.success.deleted'), { variant: 'success' });
            await fetchData();
        } catch (err) {
            enqueueSnackbar(t('rules.error.deleting'), { variant: 'error' });
            Sentry.captureException(err);
        }
    };

    const columns = [
        {
            title: t('rules.columns.name'),
            dataIndex: 'name',
            key: 'name',
            width: '20%'
        },
        {
            title: t('rules.columns.steps'),
            dataIndex: 'ruleChain',
            key: 'steps',
            width: '15%',
            render: (chain) => `${(chain || []).length} ${t('rules.columns.steps').toLowerCase()}`
        },
        {
            title: t('rules.columns.status'),
            key: 'status',
            width: '12%',
            render: (_, record) => (
                activeRule?._id === record._id ? (
                    <Tag color="green" icon={<CheckOutlined />}>{t('rules.status.active')}</Tag>
                ) : (
                    <Tag>{t('rules.status.inactive')}</Tag>
                )
            )
        },
        {
            title: t('rules.columns.description'),
            dataIndex: 'description',
            key: 'description',
            width: '25%',
            ellipsis: true
        },
        {
            title: t('rules.columns.actions'),
            key: 'actions',
            width: '28%',
            render: (_, record) => (
                <Space>
                    {activeRule?._id !== record._id && (
                        <Button
                            type="primary"
                            size="small"
                            onClick={() => handleActivateRule(record._id)}
                        >
                            {t('rules.buttons.activate')}
                        </Button>
                    )}
                    <Button
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => handleEditRule(record)}
                    >
                        {t('common.edit')}
                    </Button>
                    <Popconfirm
                        title={t('variables.delete.title')}
                        description={t('variables.delete.description')}
                        onConfirm={() => handleDeleteRule(record._id)}
                        okText={t('common.yes')}
                        cancelText={t('common.no')}
                    >
                        <Button danger size="small" icon={<DeleteOutlined />}>{t('common.delete')}</Button>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    const StepCard = ({ step, index }) => (
        <Card
            style={{ marginBottom: 16, borderLeft: '4px solid #1890ff' }}
            title={t('rules.steps.stepTitle', { index: index + 1, name: step.name })}
            extra={
                <Space>
                    <Button type="text" danger size="small" onClick={() => removeStep(step.id)}>
                        {t('rules.buttons.remove')}
                    </Button>
                </Space>
            }
        >
            <Form layout="vertical">
                <Row gutter={16}>
                    <Col xs={24} sm={12}>
                        <Form.Item label={t('rules.form.stepName')}>
                            <Input
                                value={step.name}
                                onChange={(e) => updateStep(step.id, { name: e.target.value })}
                                placeholder={t('rules.form.stepNamePlaceholder')}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item label={t('rules.form.outputVariable')}>
                            <Input
                                value={step.output}
                                onChange={(e) => updateStep(step.id, { output: e.target.value })}
                                placeholder={t('rules.form.outputPlaceholder')}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                {step.type === 'sum' && (
                    <Form.Item label={t('rules.form.variablesToSum')}>
                        <Select
                            mode="multiple"
                            value={step.variables}
                            onChange={(val) => updateStep(step.id, { variables: val })}
                            placeholder={t('rules.form.sumPlaceholder')}
                        >
                            {variables.map(v => (
                                <Select.Option key={v.key} value={v.key}>
                                    {v.name} ({v.key})
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                )}

                {step.type === 'multiply' && (
                    <>
                        <Form.Item label={t('rules.form.baseVariable')}>
                            <Select
                                value={step.variables?.[0]}
                                onChange={(val) => updateStep(step.id, { variables: [val] })}
                                placeholder={t('rules.form.basePlaceholder')}
                            >
                                {variables.map(v => (
                                    <Select.Option key={v.key} value={v.key}>
                                        {v.name} ({v.key})
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                        <Form.Item label={t('rules.form.multiplier')}>
                            <InputNumber
                                value={step.multiplier}
                                onChange={(val) => updateStep(step.id, { multiplier: val })}
                                placeholder={t('rules.form.multiplierPlaceholder')}
                                min={0}
                                step={0.01}
                                precision={4}
                                style={{ width: '100%' }}
                            />
                        </Form.Item>
                    </>
                )}

                {step.type === 'conditional' && (
                    <Form.Item label={t('rules.form.condition')}>
                        <Input disabled placeholder={t('rules.form.conditionPlaceholder')} />
                    </Form.Item>
                )}

                <Form.Item>
                    <Checkbox
                        checked={step.enabled}
                        onChange={(e) => updateStep(step.id, { enabled: e.target.checked })}
                    >
                        {t('rules.form.enableStep')}
                    </Checkbox>
                </Form.Item>
            </Form>
        </Card>
    );

    return (
        <PageLayout
            {...pageLayoutProps}
            property={propertyID}
        >
            <div style={{ maxWidth: 1400, margin: '0 auto' }}>
                <Spin spinning={fetchLoading}>
                    {!isBuilding ? (
                        <div>
                            <div style={{ marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <h3 style={{ margin: 0 }}>{t('rules.title')}</h3>
                                <Button type="primary" icon={<PlusOutlined />} onClick={handleCreateNew}>
                                    {t('rules.createNew')}
                                </Button>
                            </div>

                            {activeRule && (
                                <Alert
                                    message={t('rules.activeRule', { name: activeRule.name })}
                                    description={t('rules.activeRuleDesc')}
                                    type="info"
                                    style={{ marginBottom: 16 }}
                                />
                            )}

                            {rules.length === 0 ? (
                                <Empty description={t('rules.noRules')} style={{ marginTop: 32 }} />
                            ) : (
                                <Table
                                    columns={columns}
                                    dataSource={rules}
                                    rowKey="_id"
                                    pagination={{ pageSize: 10 }}
                                    loading={loading}
                                    size="small"
                                    scroll={{ x: 'max-content' }}
                                />
                            )}
                        </div>
                    ) : (
                        <Form form={form} onFinish={handleSaveRule} layout="vertical">
                            <Divider>{editingRuleId ? t('rules.modal.edit') : t('rules.modal.create')}</Divider>

                            <Row gutter={16}>
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        label={t('rules.form.name')}
                                        name="name"
                                        required
                                        rules={[{ required: true, message: t('rules.form.name') }]}
                                    >
                                        <Input placeholder={t('rules.form.namePlaceholder')} />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Form.Item label={t('rules.form.description')} name="description">
                                        <Input placeholder={t('rules.form.descriptionPlaceholder')} />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Divider>{t('rules.steps.title')}</Divider>

                            <Alert
                                message={t('rules.steps.buildInfo')}
                                description={t('rules.steps.buildDesc')}
                                type="info"
                                style={{ marginBottom: 16 }}
                            />

                            <div style={{ marginBottom: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                <Button onClick={() => addStep('sum')}>{t('rules.steps.addSum')}</Button>
                                <Button onClick={() => addStep('multiply')}>{t('rules.steps.addMultiply')}</Button>
                                <Tooltip title={t('rules.steps.addConditional')}>
                                    <Button disabled>{t('rules.steps.addConditional')}</Button>
                                </Tooltip>
                            </div>

                            {ruleSteps.length === 0 ? (
                                <Empty description={t('rules.steps.noSteps')} />
                            ) : (
                                <div>
                                    {ruleSteps.map((step, idx) => (
                                        <StepCard key={step.id} step={step} index={idx} />
                                    ))}
                                </div>
                            )}

                            <Divider>{t('rules.marginVat.configInfo')}</Divider>

                            <Alert
                                message={t('rules.marginVat.configInfo')}
                                description={t('rules.marginVat.configDesc')}
                                type="info"
                                style={{ marginBottom: 16 }}
                            />

                            <Row gutter={16} style={{ marginBottom: 16 }}>
                                <Col xs={24} sm={8}>
                                    <h4>{t('rules.marginVat.marginSettings')}</h4>
                                    <InputNumber
                                        placeholder={t('rules.marginVat.marginPct')}
                                        min={0}
                                        max={100}
                                        precision={2}
                                        style={{ width: '100%' }}
                                        value={phases.marginValue || 0}
                                        onChange={(val) => setPhases({ ...phases, marginValue: val })}
                                    />
                                </Col>
                                <Col xs={24} sm={8}>
                                    <h4>{t('rules.marginVat.vatSettings')}</h4>
                                    <InputNumber
                                        placeholder={t('rules.marginVat.vatPct')}
                                        min={0}
                                        max={100}
                                        precision={2}
                                        style={{ width: '100%' }}
                                        value={phases.vatValue || 0}
                                        onChange={(val) => setPhases({ ...phases, vatValue: val })}
                                    />
                                </Col>
                                <Col xs={24} sm={8}>
                                    <h4>{t('rules.marginVat.calcOrder')}</h4>
                                    <Select
                                        mode="multiple"
                                        value={calculationOrder}
                                        onChange={setCalculationOrder}
                                        placeholder={t('rules.marginVat.calcOrder')}
                                        options={[
                                            { label: t('rules.marginVat.baseAmount'), value: 'baseAmount' },
                                            { label: t('rules.marginVat.margin'), value: 'margin' },
                                            { label: t('rules.marginVat.vat'), value: 'vat' }
                                        ]}
                                    />
                                </Col>
                            </Row>

                            <Form.Item style={{ marginTop: 24 }}>
                                <Space>
                                    <Button type="primary" htmlType="submit" loading={loading}>
                                        {editingRuleId ? t('rules.buttons.update') : t('rules.buttons.create')}
                                    </Button>
                                    <Button
                                        onClick={() => {
                                            setIsBuilding(false);
                                            setEditingRuleId(null);
                                            setRuleSteps([]);
                                            form.resetFields();
                                        }}
                                    >
                                        {t('rules.buttons.cancel')}
                                    </Button>
                                </Space>
                            </Form.Item>
                        </Form>
                    )}
                </Spin>
            </div>
        </PageLayout>
    );
};

export default RuleBuilder;
